import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Upload, Loader2, AlertCircle, TrendingUp, Copy, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const PRICING_TIERS = {
  basic: { fee: 49, max_budget: 20000, max_rsvps: 25, name: "Basic Plan", rank: 1 },
  standard: { fee: 99, max_budget: 100000, max_rsvps: 60, name: "Standard Plan", rank: 2 },
  premium: { fee: 199, max_budget: 500000, max_rsvps: 200, name: "Premium Plan", rank: 3 },
  enterprise: { fee: 399, max_budget: 1000000, max_rsvps: 500, name: "Enterprise Plan", rank: 4 }
};

const PlanUpgradeModal = ({ isOpen, onClose, event, token, onUpgradeSuccess }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [paymentProof, setPaymentProof] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Calculate current plan based on budget
  const totalBudget = event?.contribution_goals?.reduce((sum, g) => sum + g.target_amount, 0) || 0;
  let currentPlanType = 'basic';
  if (totalBudget <= 20000) currentPlanType = 'basic';
  else if (totalBudget <= 100000) currentPlanType = 'standard';
  else if (totalBudget <= 500000) currentPlanType = 'premium';
  else currentPlanType = 'enterprise';

  const currentPlan = PRICING_TIERS[currentPlanType];

  // Get available upgrade options (only higher plans)
  const availableUpgrades = Object.entries(PRICING_TIERS)
    .filter(([key, plan]) => plan.rank > currentPlan.rank)
    .map(([key, plan]) => ({ key, ...plan }));

  const selectedPlanDetails = selectedPlan ? PRICING_TIERS[selectedPlan] : null;
  const upgradeCost = selectedPlanDetails ? selectedPlanDetails.fee - currentPlan.fee : 0;

  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      setPaymentProof(response.data.photo_url);
      toast.success('Payment proof uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload payment proof');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan to upgrade to');
      return;
    }

    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }

    if (!transactionDate) {
      toast.error('Please select transaction date');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/events/${event.id}/upgrade-plan`,
        {
          new_plan_type: selectedPlan,
          transaction_id: transactionId,
          transaction_date: transactionDate,
          payment_proof: paymentProof
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(
        `Plan upgrade submitted successfully! Pay ₹${upgradeCost} to anu****@okicici. Verification time: 3-6 hours (8 AM-8 PM) or 6-10 hours (8 PM-8 AM).`,
        { duration: 8000 }
      );
      
      if (onUpgradeSuccess) {
        onUpgradeSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit upgrade request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPlan('');
    setTransactionId('');
    setTransactionDate('');
    setPaymentProof('');
    onClose();
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-contribo-navy flex items-center">
            <TrendingUp className="mr-2 h-6 w-6 text-contribo-gold" />
            Upgrade Event Plan
          </DialogTitle>
          <DialogDescription>
            Upgrade to a higher plan for more RSVPs and higher budget limits
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Plan */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Current Plan</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-contribo-navy">{currentPlan.name}</p>
                <p className="text-sm text-gray-600">₹{currentPlan.fee} paid</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Features</p>
                <p className="text-sm font-semibold text-gray-800">
                  Up to {currentPlan.max_rsvps} RSVPs
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  Budget: ₹{currentPlan.max_budget.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {availableUpgrades.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You're already on the highest plan (Enterprise). No upgrades available.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Select Upgrade Plan */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Select New Plan</Label>
                <div className="grid grid-cols-1 gap-3">
                  {availableUpgrades.map((plan) => {
                    const cost = plan.fee - currentPlan.fee;
                    return (
                      <button
                        key={plan.key}
                        onClick={() => setSelectedPlan(plan.key)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedPlan === plan.key
                            ? 'border-contribo-gold bg-contribo-gold bg-opacity-10'
                            : 'border-gray-200 hover:border-contribo-navy'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900">{plan.name}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              Up to {plan.max_rsvps} RSVPs | Budget: ₹{plan.max_budget.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-600">Upgrade Cost</p>
                            <p className="text-xl font-bold text-contribo-navy">₹{cost}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedPlan && (
                <>
                  {/* Payment Instructions */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm">
                      <strong>📱 Payment Instructions:</strong>
                      <ol className="list-decimal ml-4 mt-2 space-y-1">
                        <li>Pay ₹{upgradeCost} upgrade fee via any payment method</li>
                        <li>Enter your transaction ID and date below</li>
                        <li>Upload payment proof (optional but recommended)</li>
                        <li>Admin will verify within 3-6 hours (8 AM-8 PM) or 6-10 hours (8 PM-8 AM)</li>
                        <li>Contributions will be blocked until upgrade is approved</li>
                      </ol>
                    </AlertDescription>
                  </Alert>

                  {/* Transaction ID */}
                  <div>
                    <Label htmlFor="transaction-id" className="text-sm font-semibold mb-2 block">
                      Transaction ID / UPI Reference Number *
                    </Label>
                    <Input
                      id="transaction-id"
                      type="text"
                      placeholder="Enter UPI transaction ID"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  {/* Transaction Date */}
                  <div>
                    <Label htmlFor="transaction-date" className="text-sm font-semibold mb-2 block">
                      Transaction Date *
                    </Label>
                    <Input
                      id="transaction-date"
                      type="date"
                      value={transactionDate}
                      onChange={(e) => setTransactionDate(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  {/* Payment Proof Upload */}
                  <div>
                    <Label htmlFor="payment-proof" className="text-sm font-semibold mb-2 block">
                      Payment Proof (Screenshot - Recommended)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="payment-proof"
                        type="file"
                        accept="image/*"
                        onChange={handlePaymentProofUpload}
                        disabled={uploading}
                        className="w-full"
                      />
                      {uploading && <Loader2 className="h-5 w-5 animate-spin text-contribo-navy" />}
                    </div>
                    {paymentProof && (
                      <p className="text-sm text-green-600 mt-2">✓ Payment proof uploaded</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          {availableUpgrades.length > 0 && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedPlan}
              className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Upgrade Request'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanUpgradeModal;
